import { useQuery } from "@apollo/client";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  MenuItem,
  Card,
  CardContent,
  CardHeader,
  Alert,
} from "@mui/material";
import { ReactElement, useState, useCallback, useMemo, ReactNode } from "react";

import {
  ListPersonalizedGifsQuery,
  ListPersonalizedGifsQueryVariables,
} from "~frontend/__generated__/graphql";
import { InternalLink } from "~frontend/components/Link";
import Loading from "~frontend/components/Loading";
import Select from "~frontend/components/forms/FormSelect";
import { GifPreview } from "~frontend/components/personalizedGifs/GifPreview";
import { PersonalizedGif } from "~frontend/components/personalizedGifs/types";
import { LIST_PERSONALIZED_GIFS } from "~frontend/graphql/personalizedGifs";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import { palette } from "~frontend/theme";

import { CampaignForEditor } from "../types";

export function SelectPersonalizedGifDialog({
  campaign,
  open,
  onClose,
  onSubmit,
}: {
  campaign: CampaignForEditor;
  open: boolean;
  onClose: () => void;
  onSubmit: (template: PersonalizedGif) => Promise<void>;
}): ReactElement {
  const { subaccount, subaccountLink } = useSubaccount()!;

  const listPersonalizedGifsQuery = useQuery<
    ListPersonalizedGifsQuery,
    ListPersonalizedGifsQueryVariables
  >(LIST_PERSONALIZED_GIFS, {
    variables: { input: { subaccountId: subaccount.id } },
  });

  const [selectedPersonalizedGifId, setSelectedPersonalizedGifId] = useState<
    string | null
  >(null);
  const selectedPersonalizedGif = useMemo(
    () =>
      listPersonalizedGifsQuery.data?.listPersonalizedGifs.find(
        personalizedGif => personalizedGif.id === selectedPersonalizedGifId
      ),
    [listPersonalizedGifsQuery, selectedPersonalizedGifId]
  );

  const missingRequiredContactFields = useMemo(() => {
    const campaignContactFields = new Set(
      campaign.contactFields.map(contactField => contactField.name)
    );
    return (
      selectedPersonalizedGif?.creatomateTemplate.requiredContactFields
        .map(field => field.name)
        .filter(fieldName => !campaignContactFields.has(fieldName)) ?? []
    );
  }, [campaign, selectedPersonalizedGif]);

  const renderMissingRequiredContactFields = useMemo(() => {
    const nodes = missingRequiredContactFields.map(field => (
      <strong>
        {"{"}
        {field}
        {"}"}
      </strong>
    ));
    if (missingRequiredContactFields.length === 0) {
      return <></>;
    } else if (missingRequiredContactFields.length === 1) {
      return nodes[0];
    } else if (missingRequiredContactFields.length === 2) {
      return <Join nodes={nodes} separator=" and " />;
    } else {
      return (
        <>
          <Join nodes={nodes.slice(0, nodes.length - 1)} separator=", " />, and{" "}
          {nodes[nodes.length - 1]}
        </>
      );
    }
  }, [missingRequiredContactFields]);

  const handleCancel = useCallback(() => {
    onClose();
    setSelectedPersonalizedGifId(null);
  }, [onClose, setSelectedPersonalizedGifId]);

  const [submitting, setSubmitting] = useState(false);
  const handleSubmit = useCallback(async () => {
    if (!selectedPersonalizedGif) {
      return;
    }

    setSubmitting(true);
    await onSubmit(selectedPersonalizedGif);
    setSubmitting(false);
    setSelectedPersonalizedGifId(null);
  }, [onSubmit, selectedPersonalizedGif, setSelectedPersonalizedGifId]);

  return (
    <Dialog open={open}>
      <DialogTitle>Add a Personalized GIF</DialogTitle>
      <DialogContent>
        {listPersonalizedGifsQuery.data ? (
          <>
            <Typography variant="body1">
              Select one of your templates to use in this campaign. No more than
              two personalized gifs can be used in a campaign (one per variant).
            </Typography>
            <Select
              fullWidth
              label="GIF Template"
              value={selectedPersonalizedGifId ?? ""}
              onChange={event =>
                setSelectedPersonalizedGifId(event.target.value)
              }
            >
              {listPersonalizedGifsQuery.data.listPersonalizedGifs.length ===
                0 && (
                <MenuItem disabled>
                  No personalized GIFs have been created
                </MenuItem>
              )}
              {listPersonalizedGifsQuery.data.listPersonalizedGifs.map(
                personalizedGif => (
                  <MenuItem key={personalizedGif.id} value={personalizedGif.id}>
                    {personalizedGif.name}
                  </MenuItem>
                )
              )}
            </Select>
            {selectedPersonalizedGif && (
              <>
                <Card sx={{ border: "none" }}>
                  <CardHeader title="Preview" />
                  <CardContent
                    sx={{
                      backgroundColor: palette.lightestGray,
                      maxHeight: "500px",
                      overflow: "scroll",
                    }}
                  >
                    <GifPreview
                      personalizedGifId={selectedPersonalizedGif.id}
                      pauseOnMouseOff={false}
                      sx={{
                        height: "300px",
                        width: "300px",
                        display: "block",
                        marginLeft: "auto",
                        marginRight: "auto",
                      }}
                    />
                  </CardContent>
                </Card>
                {missingRequiredContactFields.length > 0 && (
                  <Alert severity="error" icon={false}>
                    Warning: This template includes the contact fields{" "}
                    {renderMissingRequiredContactFields}, which aren't mapped on
                    this campaign. To use this template, make sure those contact
                    fields are included on the{" "}
                    <InternalLink
                      to={subaccountLink(
                        `/campaigns/${campaign.shortId}/edit/mapping`
                      )}
                    >
                      Contact Mapping
                    </InternalLink>{" "}
                    step.
                  </Alert>
                )}
              </>
            )}
          </>
        ) : (
          <Loading />
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={handleCancel}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={
            !selectedPersonalizedGif ||
            missingRequiredContactFields.length > 0 ||
            submitting
          }
          onClick={handleSubmit}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function Join({
  nodes,
  separator,
}: {
  nodes: ReactNode[];
  separator: ReactNode;
}): ReactElement {
  return (
    <>
      {[
        ...nodes.slice(0, nodes.length - 1).flatMap(node => [node, separator]),
        nodes[nodes.length - 1],
      ]}
    </>
  );
}
